import React, { useEffect, useState } from 'react';
import './index.scss';
import classNames from 'classnames';
import i18n from 'i18next';
import { getCampaignPeriodTypes } from '../../../utils/enums.js';
import { isRTL } from '../../../services/userModel.js';
import NumberInput from '../NumberInput/NumberInput.jsx';
import {
  calculateStringTimespan,
  calculateNumber,
  getPeriodByDuration,
  handleMinValueError,
  SimpleInputItem,
  SimpleItemSelect,
} from '../SectionItemAudienceFilter/audienceFilterHelpers.js';
import {getCurrencyValue} from '../../../services/restaurant.js';
import SwitchControl from '../SwitchControl/SwitchControl.jsx';
import {getDeliveryTypes} from "../../../utils/constants.js";

const getBonusVoucherModel = data => ({
  bonus_name: data?.bonus_info?.bonus_name || '', //required
  bonus: data?.bonus_info?.bonus || 0.0, //required
  bonus_expiration: data?.bonus_info?.bonus_expiration || '00:00:00', //duration
  checkout_limit_percent: data?.bonus_info?.checkout_limit_percent || 0.0, //required
  delivery_type: data?.bonus_info?.delivery_type || null, //required
  end_of_day: data?.bonus_info?.end_of_day || false,
  voucher_requires_activation: data?.bonus_info?.voucher_requires_activation || false,
});

const SectionItemBonusVoucher = ({ item, column, onPropChange }) => {
  const [periodValue, setPeriodValue] = useState(null);
  const [period, setPeriod] = useState(null);
  const [bonusVoucher, setBonusVoucher] = useState(getBonusVoucherModel(item));
  const [isVoucherActive, setIsVoucherActive] = useState(column.zeroValueIsOff ? !!item?.bonus_info?.bonus : !!item?.bonus_info);
  const checkoutLimitPercent = +(item?.bonus_info?.checkout_limit_percent * 100).toFixed(0);
  const [voucherRequiresActivation, setVoucherRequiresActivation] = useState(() => item.voucher_requires_activation);

  const handleToggleVoucherActive = () => {
    setIsVoucherActive(!isVoucherActive);
  };

  const handleToggleVoucherRequireActivation = () => {
    const newValue = !voucherRequiresActivation
    setVoucherRequiresActivation(newValue);
    handleChange(newValue, 'voucher_requires_activation');
  };

  useEffect(() => {
    if(column.disable()) {
      return;
    }
    if (isVoucherActive) {
      onPropChange({ [column.accessor]: bonusVoucher });
    } else {
      onPropChange({ [column.accessor]: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVoucherActive, bonusVoucher]);

  useEffect(() => {
    if (period && !isVoucherActive) return;
    const newPeriod = getPeriodByDuration(item.bonus_info?.bonus_expiration);
    setPeriod(newPeriod);
    setPeriodValue(calculateNumber(item.bonus_info?.bonus_expiration, newPeriod));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  useEffect(() => {
    if (!period || periodValue === undefined || !isVoucherActive) return;
    const newModel = { ...item.bonus_info, bonus_expiration: calculateStringTimespan(periodValue, period) };
    setBonusVoucher(newModel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodValue, period]);

  const handleChange = (value, key) => {
    if(column.disable()) {
      return;
    }

    const newValue = value?.key !== undefined ? value?.key : value;
    let newModel = { ...item.bonus_info, [key]: newValue };

    if(key !== 'voucher_requires_activation') {
      newModel = { ...newModel, voucher_requires_activation: voucherRequiresActivation };
    }

    if (key === 'bonus' && +newValue < 0) return handleMinValueError(0);

    if (key === 'checkout_limit_percent' && newValue) {
      newModel = { ...item.bonus_info, [key]: newValue / 100 };
    }

    setBonusVoucher(newModel);
  };

  if (item?.audience_filter === null) {
    return null;
  }

  return (
    <div className={classNames('sectionItemBonusVoucher', { rtl: isRTL(), disable: column.disable() })}>
      <div className={classNames('bonusVoucherItem')}>
        <span>{i18n.t('settings.campaigns.BONUS_VOUCHER_LABEL')}</span>
        <SwitchControl
          id={`${column.accessor} ${column.title}`}
          value={isVoucherActive}
          isNoRtl={column.isNoRtl}
          tooltipText={column.tooltipText}
          onClick={handleToggleVoucherActive}
        />
      </div>
      {isVoucherActive && (
        <div className={classNames('bonusVoucherSection')}>
          <div className={classNames('bonusVoucherItem')}>
            <span>{i18n.t('settings.campaigns.BONUS_VOUCHER_NAME_DESCRIPTION')}</span>
            <SimpleInputItem
              isError={!item?.bonus_info?.bonus_name}
              width={'420px'}
              type={'text'}
              value={item?.bonus_info?.bonus_name}
              handleChange={value => handleChange(value, 'bonus_name')}
            />
          </div>
          <div className={classNames('bonusVoucherItem')}>
            <span>{i18n.t('settings.campaigns.BONUS_VOUCHER_VALUE_LABEL')}</span>
            <div className={classNames('sectionItemNumber')} style={{ width: '95px' }}>
              <div className={classNames('inputItem withCurIcon')}>
                <div className={'curIcon'}>{getCurrencyValue()}</div>
                <NumberInput value={item?.bonus_info?.bonus} onChange={value => handleChange(value, 'bonus')} />
              </div>
            </div>
            <span>{i18n.t('settings.campaigns.BONUS_VOUCHER_VALIDITY_PERIOD_LABEL')}</span>
            <div className={classNames('sectionItemNumber')} style={{ width: '60px' }}>
              <div className={classNames('inputItem', { isError: !periodValue })}>
                <NumberInput
                  value={periodValue || 0}
                  onChange={value => {
                    if (value < 0) return handleMinValueError(0);
                    setPeriodValue(value);
                  }}
                />
              </div>
            </div>
            <SimpleItemSelect
              width={'135px'}
              value={period}
              values={getCampaignPeriodTypes()}
              handleChange={value => setPeriod(value.key)}
            />
            <span>{i18n.t('settings.campaigns.BONUS_VOUCHER_UP_TO_LABEL')}</span>
            <div className={classNames('sectionItemNumber')} style={{ width: '90px' }}>
              <div
                className={classNames('inputItem', { isError: checkoutLimitPercent < 1 || checkoutLimitPercent > 100 })}
              >
                <NumberInput
                  value={checkoutLimitPercent}
                  onChange={value => handleChange(+value || 0, 'checkout_limit_percent')}
                />
                <div className={classNames('percent')}>%</div>
              </div>
            </div>
            <span>{i18n.t('settings.campaigns.BONUS_VOUCHER_AFTER_PERCENT_LABEL')}</span>
           <SwitchControl
          id={`isVoucherTiiEndOfDaySwitcher`}
          value={item?.bonus_info?.end_of_day}
          isNoRtl={column.isNoRtl}
          tooltipText={column.tooltipText}
          onClick={value => handleChange(value, 'end_of_day')}
          />
        <span>{i18n.t('settings.campaigns.CAMPAIGN_EXPIR_END_OF_DAY')}</span>
          </div>
          <div className={classNames('bonusVoucherItem')}>
            <span>{i18n.t('settings.campaigns.BONUS_VOUCHER_DELIVERY_TYPE')}</span>
            <SimpleItemSelect
                width={'150px'}
                value={item?.bonus_info?.delivery_type}
                values={[{key: null, value: i18n.t('settings.campaigns.BONUS_VOUCHER_ALL_DELIVERY_TYPES')}].concat(getDeliveryTypes())}
                handleChange={value => handleChange(value.key, 'delivery_type')}
            />
          </div>
          <div className={classNames('bonusVoucherItem')}>
            <span>{i18n.t('settings.campaigns.CAMPAIGN_EDIT_PROMO_VOUCHER_REQUIRES_ACTIVATION')}</span>
            <SwitchControl
              id={`${column.accessor}-${column.title}-requireConfirm`}
              value={voucherRequiresActivation}
              isNoRtl={column.isNoRtl}
              onClick={handleToggleVoucherRequireActivation}
            />
          </div>
        </div>
      )}

    </div>
  );
};

export default SectionItemBonusVoucher;
